import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { supabase } from '../lib/supabase';
import { hashPassword } from '../lib/auth';
import { Crown, Users, CircleCheck as CheckCircle, Circle as XCircle, RefreshCw, Plus, Eye, EyeOff, X, UserPlus, CreditCard as Edit, Trash2, Key, Store, TriangleAlert as AlertTriangle, Link, Unlink, Settings, Truck, Package, Brain, ChevronDown, ChevronUp, Download, Building2 } from 'lucide-react';
import { CompanyAccessModal } from '../components/admin/CompanyAccessModal';

interface Admin {
  id: string;
  email: string;
  full_name: string;
  role: string;
  is_active: boolean;
  created_at: string;
  allowed_pages: string[];
  can_access_sales_history?: boolean;
}

interface AdminCredentialStatus {
  admin_id: string;
  email: string;
  full_name: string;
  is_active: boolean;
  is_configured: boolean;
  client_id_masked: string | null;
  scopes: string | null;
  configured_at: string | null;
  has_connected_shop: boolean;
  shop_domain: string | null;
  requires_reconnect: boolean;
  credential_source: string;
}

interface AdminPostNLCredentialStatus {
  admin_id: string;
  email: string;
  full_name: string;
  is_active: boolean;
  is_configured: boolean;
  customer_code_masked: string | null;
  environment: string | null;
  configured_at: string | null;
  credentials_active: boolean;
}

interface AdminBrevoCredentialStatus {
  admin_id: string;
  email: string;
  full_name: string;
  is_active: boolean;
  is_configured: boolean;
  sender_email_masked: string | null;
  sender_name: string | null;
  configured_at: string | null;
  credentials_active: boolean;
  can_access_sales_history: boolean;
}

type ActiveTab = 'admins' | 'credentials' | 'postnl' | 'brevo' | 'ai_log';

export const SuperAdminPanel: React.FC = () => {
  const { user, isSuperAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState<ActiveTab>('admins');
  const [companyAccessAdmin, setCompanyAccessAdmin] = useState<{ id: string; name: string } | null>(null);
  const [admins, setAdmins] = useState<Admin[]>([]);
  const [loading, setLoading] = useState(false);
  const [showCreateAdmin, setShowCreateAdmin] = useState(false);
  const [showEditAdmin, setShowEditAdmin] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState<Admin | null>(null);
  const [createAdminLoading, setCreateAdminLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [newAdminData, setNewAdminData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    full_name: ''
  });
  const [editAdminData, setEditAdminData] = useState({
    email: '',
    full_name: '',
    password: '',
    confirmPassword: '',
    is_active: true
  });

  const [adminCredentials, setAdminCredentials] = useState<AdminCredentialStatus[]>([]);
  const [credentialsLoading, setCredentialsLoading] = useState(false);
  const [showConfigureCredentials, setShowConfigureCredentials] = useState(false);
  const [configuringAdmin, setConfiguringAdmin] = useState<AdminCredentialStatus | null>(null);
  const [credentialFormData, setCredentialFormData] = useState({
    client_id: '',
    client_secret: '',
    webhook_secret: '',
    scopes: 'read_products,write_products,read_inventory,write_inventory,read_locations,read_orders,read_assigned_fulfillment_orders,write_assigned_fulfillment_orders,write_fulfillments,read_merchant_managed_fulfillment_orders,write_merchant_managed_fulfillment_orders'
  });
  const [showClientSecret, setShowClientSecret] = useState(false);
  const [credentialSaving, setCredentialSaving] = useState(false);

  const [postnlCredentials, setPostnlCredentials] = useState<AdminPostNLCredentialStatus[]>([]);
  const [postnlCredentialsLoading, setPostnlCredentialsLoading] = useState(false);
  const [showConfigurePostNL, setShowConfigurePostNL] = useState(false);
  const [configuringPostNLAdmin, setConfiguringPostNLAdmin] = useState<AdminPostNLCredentialStatus | null>(null);
  const [postnlFormData, setPostnlFormData] = useState({
    api_key: '',
    customer_code: '',
    customer_number: '',
    collection_location: '',
    contact_person: '',
    email: '',
    environment: 'sandbox' as 'sandbox' | 'production'
  });
  const [showPostNLApiKey, setShowPostNLApiKey] = useState(false);
  const [postnlSaving, setPostnlSaving] = useState(false);

  const [brevoCredentials, setBrevoCredentials] = useState<AdminBrevoCredentialStatus[]>([]);
  const [brevoCredentialsLoading, setBrevoCredentialsLoading] = useState(false);
  const [showConfigureBrevo, setShowConfigureBrevo] = useState(false);
  const [configuringBrevoAdmin, setConfiguringBrevoAdmin] = useState<AdminBrevoCredentialStatus | null>(null);
  const [brevoFormData, setBrevoFormData] = useState({ api_key: '', sender_name: '', sender_email: '' });
  const [showBrevoApiKey, setShowBrevoApiKey] = useState(false);
  const [brevoSaving, setBrevoSaving] = useState(false);

  interface AiLogEntry {
    id: string;
    admin_id: string;
    shopify_order_id: number | null;
    order_number: string | null;
    country_code: string | null;
    original_address: Record<string, unknown>;
    corrected_address: Record<string, unknown>;
    diagnostics: { confidence?: string; wasModified?: boolean; issues?: Array<{ field: string; severity: string; message: string }> } | null;
    created_at: string;
    admin_email?: string;
    source: string | null;
  }

  const [aiLogs, setAiLogs] = useState<AiLogEntry[]>([]);
  const [aiLogsLoading, setAiLogsLoading] = useState(false);
  const [aiLogsPage, setAiLogsPage] = useState(1);
  const [aiLogsTotalCount, setAiLogsTotalCount] = useState(0);
  const [aiLogsCountryFilter, setAiLogsCountryFilter] = useState('');
  const [aiLogsExpanded, setAiLogsExpanded] = useState<Set<string>>(new Set());
  const AI_LOGS_PAGE_SIZE = 25;

  const fetchAiLogs = async () => {
    setAiLogsLoading(true);
    try {
      let countQuery = supabase.from('ai_address_log').select('*', { count: 'exact', head: true });
      let dataQuery = supabase.from('ai_address_log').select('*')
        .order('created_at', { ascending: false })
        .range((aiLogsPage - 1) * AI_LOGS_PAGE_SIZE, aiLogsPage * AI_LOGS_PAGE_SIZE - 1);

      if (aiLogsCountryFilter) {
        countQuery = countQuery.eq('country_code', aiLogsCountryFilter.toUpperCase());
        dataQuery = dataQuery.eq('country_code', aiLogsCountryFilter.toUpperCase());
      }

      const [{ count }, { data, error }] = await Promise.all([countQuery, dataQuery]);
      if (error) throw error;

      const adminIds = [...new Set((data || []).map((d: AiLogEntry) => d.admin_id))];
      let adminMap: Record<string, string> = {};
      if (adminIds.length > 0) {
        const { data: adminUsers } = await supabase.from('users').select('id, email').in('id', adminIds);
        adminMap = Object.fromEntries((adminUsers || []).map((u: { id: string; email: string }) => [u.id, u.email]));
      }

      setAiLogs((data || []).map((d: AiLogEntry) => ({ ...d, admin_email: adminMap[d.admin_id] || d.admin_id })));
      setAiLogsTotalCount(count || 0);
    } catch (err) {
      console.error('Failed to fetch AI logs:', err);
    } finally {
      setAiLogsLoading(false);
    }
  };

  const toggleAiLogExpand = (id: string) => {
    setAiLogsExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const [aiLogsDownloading, setAiLogsDownloading] = useState(false);
  const [aiLogsSelected, setAiLogsSelected] = useState<Set<string>>(new Set());
  const [aiLogsDeleting, setAiLogsDeleting] = useState(false);

  const downloadAiTrainingLog = async () => {
    setAiLogsDownloading(true);
    try {
      let query = supabase.from('ai_address_log').select('*').order('created_at', { ascending: true });
      if (aiLogsCountryFilter) query = query.eq('country_code', aiLogsCountryFilter.toUpperCase());

      const PAGE = 1000;
      let all: AiLogEntry[] = [];
      let offset = 0;
      let done = false;
      while (!done) {
        const { data, error } = await query.range(offset, offset + PAGE - 1);
        if (error) throw error;
        if (!data || data.length === 0) { done = true; break; }
        all = all.concat(data as AiLogEntry[]);
        if (data.length < PAGE) done = true;
        else offset += PAGE;
      }

      const lines = all.map((log) => JSON.stringify({
        input: { address: log.original_address, country: log.country_code },
        expected_output: { normalized: log.corrected_address },
        diagnostics: log.diagnostics,
        source: log.source || null,
        order: log.order_number || (log.shopify_order_id ? `#${log.shopify_order_id}` : null),
        date: log.created_at,
      }));

      const blob = new Blob([lines.join('\n')], { type: 'application/jsonl' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ai-address-training-${new Date().toISOString().slice(0, 10)}.jsonl`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download AI training log:', err);
    } finally {
      setAiLogsDownloading(false);
    }
  };

  const deleteSelectedAiLogs = async () => {
    if (aiLogsSelected.size === 0) return;
    const ids = [...aiLogsSelected];
    if (!confirm(`Delete ${ids.length} log entr${ids.length === 1 ? 'y' : 'ies'}? This cannot be undone.`)) return;
    setAiLogsDeleting(true);
    try {
      const BATCH = 100;
      for (let i = 0; i < ids.length; i += BATCH) {
        const batch = ids.slice(i, i + BATCH);
        const { error } = await supabase.from('ai_address_log').delete().in('id', batch);
        if (error) throw error;
      }
      setAiLogsSelected(new Set());
      fetchAiLogs();
    } catch (err) {
      console.error('Failed to delete AI logs:', err);
    } finally {
      setAiLogsDeleting(false);
    }
  };

  const deleteAllAiLogs = async () => {
    if (!confirm(`Delete ALL ${aiLogsTotalCount} log entries${aiLogsCountryFilter ? ` for country "${aiLogsCountryFilter.toUpperCase()}"` : ''}? This cannot be undone.`)) return;
    setAiLogsDeleting(true);
    try {
      let query = supabase.from('ai_address_log').delete();
      if (aiLogsCountryFilter) query = query.eq('country_code', aiLogsCountryFilter.toUpperCase());
      else query = query.gte('created_at', '1970-01-01');
      const { error } = await query;
      if (error) throw error;
      setAiLogsSelected(new Set());
      fetchAiLogs();
    } catch (err) {
      console.error('Failed to delete all AI logs:', err);
    } finally {
      setAiLogsDeleting(false);
    }
  };

  const toggleAiLogSelect = (id: string) => {
    setAiLogsSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAiLogSelectAll = () => {
    if (aiLogsSelected.size === aiLogs.length) {
      setAiLogsSelected(new Set());
    } else {
      setAiLogsSelected(new Set(aiLogs.map(l => l.id)));
    }
  };

  useEffect(() => {
    if (activeTab === 'ai_log') fetchAiLogs();
  }, [activeTab, aiLogsPage, aiLogsCountryFilter]);

  if (!isSuperAdmin) {
    return (
      <div className="p-6">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-600 dark:text-red-400">Access denied. SuperAdmin privileges required.</p>
        </div>
      </div>
    );
  }

  useEffect(() => {
    loadAdmins();
    loadAdminCredentials();
    loadPostNLCredentials();
    loadBrevoCredentials();
  }, []);

  const loadAdminCredentials = async () => {
    setCredentialsLoading(true);
    try {
      const { data, error } = await supabase.rpc('get_all_admin_credentials_status', {
        p_caller_id: user?.id,
        p_caller_role: user?.role
      });

      if (error) {
        console.error('Error loading credentials:', error);
        return;
      }

      if (data?.success && data?.admins) {
        setAdminCredentials(data.admins);
      }
    } catch (error) {
      console.error('Error loading credentials:', error);
    } finally {
      setCredentialsLoading(false);
    }
  };

  const loadPostNLCredentials = async () => {
    setPostnlCredentialsLoading(true);
    try {
      const { data, error } = await supabase.rpc('get_all_admin_postnl_credentials_status', {
        p_caller_id: user?.id,
        p_caller_role: user?.role
      });

      if (error) {
        console.error('Error loading PostNL credentials:', error);
        return;
      }

      if (data?.success && data?.admins) {
        setPostnlCredentials(data.admins);
      }
    } catch (error) {
      console.error('Error loading PostNL credentials:', error);
    } finally {
      setPostnlCredentialsLoading(false);
    }
  };

  const handleConfigurePostNL = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!configuringPostNLAdmin) return;

    if (!postnlFormData.api_key.trim()) {
      alert('API Key is required');
      return;
    }

    if (!postnlFormData.customer_code.trim()) {
      alert('Customer Code is required');
      return;
    }

    setPostnlSaving(true);
    try {
      const { data, error } = await supabase.rpc('configure_admin_postnl_credentials', {
        p_caller_id: user?.id,
        p_caller_role: user?.role,
        p_admin_id: configuringPostNLAdmin.admin_id,
        p_api_key: postnlFormData.api_key.trim(),
        p_customer_code: postnlFormData.customer_code.trim(),
        p_customer_number: postnlFormData.customer_number.trim() || null,
        p_collection_location: postnlFormData.collection_location.trim() || null,
        p_contact_person: postnlFormData.contact_person.trim() || null,
        p_email: postnlFormData.email.trim() || null,
        p_environment: postnlFormData.environment
      });

      if (error) {
        console.error('Error configuring PostNL credentials:', error);
        alert(`Failed to configure credentials: ${error.message}`);
        return;
      }

      if (!data?.success) {
        alert(data?.error || 'Failed to configure credentials');
        return;
      }

      setShowConfigurePostNL(false);
      setConfiguringPostNLAdmin(null);
      resetPostNLForm();
      await loadPostNLCredentials();
      alert('PostNL credentials configured successfully!');
    } catch (error) {
      console.error('Error configuring PostNL credentials:', error);
      alert('Failed to configure credentials');
    } finally {
      setPostnlSaving(false);
    }
  };

  const handleClearPostNLCredentials = async (adminId: string, adminName: string) => {
    if (!confirm(`Are you sure you want to clear PostNL credentials for "${adminName}"?`)) {
      return;
    }

    try {
      const { data, error } = await supabase.rpc('clear_admin_postnl_credentials', {
        p_caller_id: user?.id,
        p_caller_role: user?.role,
        p_admin_id: adminId
      });

      if (error) {
        console.error('Error clearing PostNL credentials:', error);
        alert(`Failed to clear credentials: ${error.message}`);
        return;
      }

      if (!data?.success) {
        alert(data?.error || 'Failed to clear credentials');
        return;
      }

      await loadPostNLCredentials();
      alert('PostNL credentials cleared.');
    } catch (error) {
      console.error('Error clearing PostNL credentials:', error);
      alert('Failed to clear credentials');
    }
  };

  const openPostNLConfigureModal = (admin: AdminPostNLCredentialStatus) => {
    setConfiguringPostNLAdmin(admin);
    setPostnlFormData({
      api_key: '',
      customer_code: '',
      customer_number: '',
      collection_location: '',
      contact_person: '',
      email: '',
      environment: 'sandbox'
    });
    setShowConfigurePostNL(true);
  };

  const resetPostNLForm = () => {
    setPostnlFormData({
      api_key: '',
      customer_code: '',
      customer_number: '',
      collection_location: '',
      contact_person: '',
      email: '',
      environment: 'sandbox'
    });
    setShowPostNLApiKey(false);
  };

  const handleConfigureCredentials = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!configuringAdmin) return;

    if (!credentialFormData.client_id.trim()) {
      alert('Client ID is required');
      return;
    }

    if (!credentialFormData.client_secret.trim() || credentialFormData.client_secret.length < 32) {
      alert('Client Secret is required and must be at least 32 characters');
      return;
    }

    setCredentialSaving(true);
    try {
      const { data, error } = await supabase.rpc('configure_admin_shopify_credentials', {
        p_caller_id: user?.id,
        p_caller_role: user?.role,
        p_admin_id: configuringAdmin.admin_id,
        p_client_id: credentialFormData.client_id.trim(),
        p_client_secret: credentialFormData.client_secret.trim(),
        p_webhook_secret: credentialFormData.webhook_secret.trim() || null,
        p_scopes: credentialFormData.scopes.trim() || null
      });

      if (error) {
        console.error('Error configuring credentials:', error);
        alert(`Failed to configure credentials: ${error.message}`);
        return;
      }

      if (!data?.success) {
        alert(data?.error || 'Failed to configure credentials');
        return;
      }

      setShowConfigureCredentials(false);
      setConfiguringAdmin(null);
      resetCredentialForm();
      await loadAdminCredentials();

      if (data?.requires_reconnect) {
        alert('Credentials configured. The admin will need to reconnect their Shopify store.');
      } else {
        alert('Credentials configured successfully!');
      }
    } catch (error) {
      console.error('Error configuring credentials:', error);
      alert('Failed to configure credentials');
    } finally {
      setCredentialSaving(false);
    }
  };

  const handleClearCredentials = async (adminId: string, adminName: string) => {
    if (!confirm(`Are you sure you want to clear Shopify credentials for "${adminName}"? They will use the global credentials instead.`)) {
      return;
    }

    try {
      const { data, error } = await supabase.rpc('clear_admin_shopify_credentials', {
        p_caller_id: user?.id,
        p_caller_role: user?.role,
        p_admin_id: adminId
      });

      if (error) {
        console.error('Error clearing credentials:', error);
        alert(`Failed to clear credentials: ${error.message}`);
        return;
      }

      if (!data?.success) {
        alert(data?.error || 'Failed to clear credentials');
        return;
      }

      await loadAdminCredentials();
      alert('Credentials cleared. Admin will now use global credentials.');
    } catch (error) {
      console.error('Error clearing credentials:', error);
      alert('Failed to clear credentials');
    }
  };

  const openConfigureModal = (admin: AdminCredentialStatus) => {
    setConfiguringAdmin(admin);
    setCredentialFormData({
      client_id: '',
      client_secret: '',
      webhook_secret: '',
      scopes: admin.scopes || 'read_products,write_products,read_inventory,write_inventory,read_locations,read_orders,read_assigned_fulfillment_orders,write_assigned_fulfillment_orders,write_fulfillments,read_merchant_managed_fulfillment_orders,write_merchant_managed_fulfillment_orders'
    });
    setShowConfigureCredentials(true);
  };

  const resetCredentialForm = () => {
    setCredentialFormData({
      client_id: '',
      client_secret: '',
      webhook_secret: '',
      scopes: 'read_products,write_products,read_inventory,write_inventory,read_locations,read_orders,read_assigned_fulfillment_orders,write_assigned_fulfillment_orders,write_fulfillments,read_merchant_managed_fulfillment_orders,write_merchant_managed_fulfillment_orders'
    });
    setShowClientSecret(false);
  };

  const loadBrevoCredentials = async () => {
    setBrevoCredentialsLoading(true);
    try {
      const { data, error } = await supabase.rpc('get_all_admin_brevo_credentials_status', {
        p_caller_id: user?.id,
        p_caller_role: user?.role,
      });
      if (error) { console.error('Error loading Brevo credentials:', error); return; }
      if (data?.success && data?.admins) { setBrevoCredentials(data.admins); }
    } catch (error) {
      console.error('Error loading Brevo credentials:', error);
    } finally {
      setBrevoCredentialsLoading(false);
    }
  };

  const handleConfigureBrevo = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!configuringBrevoAdmin) return;
    if (!brevoFormData.api_key.trim()) { alert('API Key is required'); return; }
    if (!brevoFormData.sender_email.trim()) { alert('Sender Email is required'); return; }

    setBrevoSaving(true);
    try {
      const { data, error } = await supabase.rpc('configure_admin_brevo_credentials', {
        p_caller_id: user?.id,
        p_caller_role: user?.role,
        p_admin_id: configuringBrevoAdmin.admin_id,
        p_api_key: brevoFormData.api_key.trim(),
        p_sender_name: brevoFormData.sender_name.trim(),
        p_sender_email: brevoFormData.sender_email.trim(),
      });
      if (error) { alert(`Failed: ${error.message}`); return; }
      if (!data?.success) { alert(data?.error || 'Failed'); return; }
      setShowConfigureBrevo(false);
      setConfiguringBrevoAdmin(null);
      setBrevoFormData({ api_key: '', sender_name: '', sender_email: '' });
      setShowBrevoApiKey(false);
      await loadBrevoCredentials();
      alert('Brevo credentials configured successfully!');
    } catch (error) {
      alert('Failed to configure credentials');
    } finally {
      setBrevoSaving(false);
    }
  };

  const handleClearBrevoCredentials = async (adminId: string, adminName: string) => {
    if (!confirm(`Clear Brevo credentials for "${adminName}"?`)) return;
    try {
      const { data, error } = await supabase.rpc('clear_admin_brevo_credentials', {
        p_caller_id: user?.id, p_caller_role: user?.role, p_admin_id: adminId,
      });
      if (error) { alert(`Failed: ${error.message}`); return; }
      if (!data?.success) { alert(data?.error || 'Failed'); return; }
      await loadBrevoCredentials();
      alert('Brevo credentials cleared.');
    } catch (error) { alert('Failed to clear credentials'); }
  };

  const handleToggleSalesHistory = async (adminId: string, currentValue: boolean) => {
    try {
      const { data, error } = await supabase.rpc('toggle_admin_sales_history_access', {
        p_caller_id: user?.id,
        p_caller_role: user?.role,
        p_admin_id: adminId,
        p_enabled: !currentValue,
      });
      if (error) { alert(`Failed: ${error.message}`); return; }
      if (!data?.success) { alert(data?.error || 'Failed'); return; }

      const targetAdmin = admins.find(a => a.id === adminId);
      if (targetAdmin) {
        const pages = targetAdmin.allowed_pages ?? [];
        const updatedPages = !currentValue
          ? [...new Set([...pages, 'sales-history'])]
          : pages.filter(p => p !== 'sales-history');
        await supabase.from('users').update({ allowed_pages: updatedPages }).eq('id', adminId);
      }

      await loadAdmins();
      await loadBrevoCredentials();
    } catch (error) { alert('Failed to toggle sales history access'); }
  };

  const loadAdmins = async () => {
    setLoading(true);

    try {
      const { data, error } = await supabase
        .from('users')
        .select('id, email, full_name, role, is_active, created_at, allowed_pages, can_access_sales_history')
        .eq('role', 'admin')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error loading admins:', error);
        alert('Failed to load admins');
        return;
      }

      setAdmins(data || []);
    } catch (error) {
      console.error('Error loading admins:', error);
      alert('Failed to load admins');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAdmin = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newAdminData.email.trim() || !newAdminData.password || !newAdminData.full_name.trim()) {
      alert('Please fill in all required fields');
      return;
    }

    if (newAdminData.password !== newAdminData.confirmPassword) {
      alert('Passwords do not match');
      return;
    }

    if (newAdminData.password.length < 8) {
      alert('Password must be at least 8 characters');
      return;
    }

    setCreateAdminLoading(true);

    try {
      const password_hash = await hashPassword(newAdminData.password);

      const { data, error } = await supabase.rpc('create_admin_user_secure', {
        p_caller_id: user?.id,
        p_caller_role: user?.role,
        p_email: newAdminData.email.trim(),
        p_password_hash: password_hash,
        p_full_name: newAdminData.full_name.trim(),
        p_company_name: null
      });

      if (error) {
        console.error('Error creating admin:', error);
        alert(`Failed to create admin: ${error.message}`);
        return;
      }

      if (!data?.success) {
        console.error('Error creating admin:', data?.error);
        alert(data?.error || 'Failed to create admin');
        return;
      }

      setNewAdminData({
        email: '',
        password: '',
        confirmPassword: '',
        full_name: ''
      });
      setShowCreateAdmin(false);

      await loadAdmins();
      alert(`Admin "${newAdminData.full_name}" created successfully!`);

    } catch (error) {
      console.error('Error creating admin:', error);
      alert('Failed to create admin. Please try again.');
    } finally {
      setCreateAdminLoading(false);
    }
  };

  const handleEditAdmin = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!editAdminData.email.trim() || !editAdminData.full_name.trim()) {
      alert('Please fill in all required fields');
      return;
    }

    if (editAdminData.password && editAdminData.password !== editAdminData.confirmPassword) {
      alert('Passwords do not match');
      return;
    }

    if (editAdminData.password && editAdminData.password.length < 8) {
      alert('Password must be at least 8 characters');
      return;
    }

    setCreateAdminLoading(true);

    try {
      const password_hash = editAdminData.password ? await hashPassword(editAdminData.password) : null;

      const { data, error } = await supabase.rpc('update_admin_user_secure', {
        p_caller_id: user?.id,
        p_caller_role: user?.role,
        p_user_id: editingAdmin!.id,
        p_email: editAdminData.email.trim(),
        p_full_name: editAdminData.full_name.trim(),
        p_password_hash: password_hash,
        p_is_active: editAdminData.is_active ?? true
      });

      if (error) {
        console.error('Error updating admin:', error);
        alert(`Failed to update admin: ${error.message}`);
        return;
      }

      if (!data?.success) {
        console.error('Error updating admin:', data?.error);
        alert(data?.error || 'Failed to update admin');
        return;
      }

      setEditAdminData({
        email: '',
        full_name: '',
        password: '',
        confirmPassword: '',
        is_active: true
      });
      setShowEditAdmin(false);
      setEditingAdmin(null);

      await loadAdmins();
      alert('Admin updated successfully!');

    } catch (error) {
      console.error('Error updating admin:', error);
      alert('Failed to update admin. Please try again.');
    } finally {
      setCreateAdminLoading(false);
    }
  };

  const handleDeleteAdmin = async (adminId: string, adminName: string) => {
    if (!confirm(`Are you sure you want to deactivate admin "${adminName}"? This will prevent them from accessing the system.`)) {
      return;
    }

    try {
      const { data, error } = await supabase.rpc('delete_admin_user_secure', {
        p_caller_id: user?.id,
        p_caller_role: user?.role,
        p_user_id: adminId
      });

      if (error) {
        console.error('Error deleting admin:', error);
        alert(`Failed to delete admin: ${error.message}`);
        return;
      }

      if (!data?.success) {
        console.error('Error deleting admin:', data?.error);
        alert(data?.error || 'Failed to delete admin');
        return;
      }

      await loadAdmins();
      alert('Admin deactivated successfully!');
    } catch (error) {
      console.error('Error deleting admin:', error);
      alert('Failed to delete admin');
    }
  };

  const openEditModal = (admin: Admin) => {
    setEditingAdmin(admin);
    setEditAdminData({
      email: admin.email,
      full_name: admin.full_name,
      password: '',
      confirmPassword: '',
      is_active: admin.is_active
    });
    setShowEditAdmin(true);
  };

  const toggleAdminStatus = async (adminId: string, currentStatus: boolean) => {
    try {
      const { data, error } = await supabase.rpc('update_admin_user_secure', {
        p_caller_id: user?.id,
        p_caller_role: user?.role,
        p_user_id: adminId,
        p_is_active: !currentStatus
      });

      if (error) {
        console.error('Error updating admin status:', error);
        alert(`Failed to update admin status: ${error.message}`);
        return;
      }

      if (!data?.success) {
        console.error('Error updating admin status:', data?.error);
        alert(data?.error || 'Failed to update admin status');
        return;
      }

      await loadAdmins();
      alert(`Admin ${!currentStatus ? 'activated' : 'deactivated'} successfully!`);
    } catch (error) {
      console.error('Error updating admin status:', error);
      alert('Failed to update admin status');
    }
  };

  const resetCreateAdminForm = () => {
    setNewAdminData({
      email: '',
      password: '',
      confirmPassword: '',
      full_name: ''
    });
    setShowCreateAdmin(false);
    setShowPassword(false);
    setShowConfirmPassword(false);
  };

  const resetEditAdminForm = () => {
    setEditAdminData({
      email: '',
      full_name: '',
      password: '',
      confirmPassword: '',
      is_active: true
    });
    setShowEditAdmin(false);
    setEditingAdmin(null);
    setShowPassword(false);
    setShowConfirmPassword(false);
  };

  const systemStats = {
    totalAdmins: admins.length,
    activeAdmins: admins.filter(a => a.is_active).length,
    inactiveAdmins: admins.filter(a => !a.is_active).length,
    recentAdmins: admins.filter(a => {
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      return new Date(a.created_at) > weekAgo;
    }).length,
  };

  return (
    <div className="p-6">
      <div className="mb-8">
        <div className="flex items-center mb-4">
          <Crown className="h-8 w-8 text-yellow-500 mr-3" />
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
            SuperAdmin Panel
          </h1>
        </div>
        <p className="text-gray-600 dark:text-gray-400">
          Manage admin users and system settings
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="card p-6">
          <div className="flex items-center">
            <Users className="h-8 w-8 text-blue-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Admins</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
                {systemStats.totalAdmins}
              </p>
            </div>
          </div>
        </div>

        <div className="card p-6">
          <div className="flex items-center">
            <CheckCircle className="h-8 w-8 text-green-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Active Admins</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
                {systemStats.activeAdmins}
              </p>
            </div>
          </div>
        </div>

        <div className="card p-6">
          <div className="flex items-center">
            <XCircle className="h-8 w-8 text-red-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Inactive Admins</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
                {systemStats.inactiveAdmins}
              </p>
            </div>
          </div>
        </div>

        <div className="card p-6">
          <div className="flex items-center">
            <Plus className="h-8 w-8 text-orange-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">New This Week</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
                {systemStats.recentAdmins}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="mb-6">
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="flex space-x-8">
            <button
              onClick={() => setActiveTab('admins')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'admins'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              <Users className="h-4 w-4 inline mr-2" />
              Admin Users
            </button>
            <button
              onClick={() => setActiveTab('credentials')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'credentials'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              <Key className="h-4 w-4 inline mr-2" />
              Shopify Credentials
            </button>
            <button
              onClick={() => setActiveTab('postnl')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'postnl'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              <Truck className="h-4 w-4 inline mr-2" />
              PostNL Settings
            </button>
            <button
              onClick={() => setActiveTab('brevo')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'brevo'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              <Package className="h-4 w-4 inline mr-2" />
              Brevo & Sales
            </button>
            <button
              onClick={() => setActiveTab('ai_log')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'ai_log'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              <Brain className="h-4 w-4 inline mr-2" />
              AI Address Log
            </button>
          </nav>
        </div>
      </div>

      {activeTab === 'admins' && (
        <>
          <div className="card p-6 mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                System Controls
              </h2>
              <div className="flex space-x-3">
                <Button onClick={() => setShowCreateAdmin(true)} variant="primary">
                  <Plus className="h-4 w-4 mr-2" />
                  Create Admin
                </Button>
                <Button onClick={loadAdmins} variant="secondary" loading={loading}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Refresh Data
                </Button>
              </div>
            </div>
          </div>

          <div className="card p-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-6">
          Admin Users
        </h2>

        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-4"></div>
            <p className="text-gray-500 dark:text-gray-400">Loading admins...</p>
          </div>
        ) : admins.length === 0 ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            <Users className="h-12 w-12 mx-auto mb-4" />
            <p>No admin users found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Admin
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Sales History
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                {admins.map((admin) => (
                  <tr key={admin.id}>
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {admin.full_name}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {admin.email}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {new Date(admin.created_at).toLocaleDateString()}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        admin.is_active
                          ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                          : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                      }`}>
                        {admin.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() => handleToggleSalesHistory(admin.id, admin.can_access_sales_history ?? false)}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          admin.can_access_sales_history
                            ? 'bg-teal-600'
                            : 'bg-gray-300 dark:bg-gray-600'
                        }`}
                      >
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow-sm ${
                          admin.can_access_sales_history ? 'translate-x-6' : 'translate-x-1'
                        }`} />
                      </button>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        <Button
                          onClick={() => openEditModal(admin)}
                          variant="secondary"
                          size="sm"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          onClick={() => setCompanyAccessAdmin({ id: admin.id, name: admin.full_name })}
                          variant="secondary"
                          size="sm"
                          title="Manage which companies this admin can switch to"
                        >
                          <Building2 className="h-4 w-4" />
                        </Button>
                        <Button
                          onClick={() => toggleAdminStatus(admin.id, admin.is_active)}
                          variant={admin.is_active ? 'danger' : 'success'}
                          size="sm"
                        >
                          {admin.is_active ? 'Deactivate' : 'Activate'}
                        </Button>
                        <Button
                          onClick={() => handleDeleteAdmin(admin.id, admin.full_name)}
                          variant="danger"
                          size="sm"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
          </div>
        </>
      )}

      {companyAccessAdmin && (
        <CompanyAccessModal
          adminId={companyAccessAdmin.id}
          adminName={companyAccessAdmin.name}
          onClose={() => setCompanyAccessAdmin(null)}
        />
      )}

      {activeTab === 'credentials' && (
        <div className="card p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                Shopify App Credentials
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Configure unique Shopify App credentials for each admin account
              </p>
            </div>
            <Button onClick={loadAdminCredentials} variant="secondary" loading={credentialsLoading}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>

          {credentialsLoading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-6 w-6 animate-spin text-blue-500" />
            </div>
          ) : adminCredentials.length === 0 ? (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              No admin accounts found
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-700 dark:text-gray-300">Admin</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-700 dark:text-gray-300">Credentials</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-700 dark:text-gray-300">Store</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-700 dark:text-gray-300">Status</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-gray-700 dark:text-gray-300">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {adminCredentials.map((admin) => (
                    <tr key={admin.admin_id} className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                      <td className="py-4 px-4">
                        <div>
                          <p className="font-medium text-gray-900 dark:text-gray-100">{admin.full_name || 'Unnamed'}</p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">{admin.email}</p>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        {admin.is_configured ? (
                          <div>
                            <div className="flex items-center space-x-2">
                              <Key className="h-4 w-4 text-green-500" />
                              <span className="text-sm text-gray-900 dark:text-gray-100">Custom App</span>
                            </div>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                              ID: {admin.client_id_masked}
                            </p>
                            {admin.configured_at && (
                              <p className="text-xs text-gray-400 dark:text-gray-500">
                                Set {new Date(admin.configured_at).toLocaleDateString()}
                              </p>
                            )}
                          </div>
                        ) : (
                          <div className="flex items-center space-x-2">
                            <Settings className="h-4 w-4 text-gray-400" />
                            <span className="text-sm text-gray-500 dark:text-gray-400">Using Global</span>
                          </div>
                        )}
                      </td>
                      <td className="py-4 px-4">
                        {admin.has_connected_shop ? (
                          <div>
                            <div className="flex items-center space-x-2">
                              <Store className="h-4 w-4 text-blue-500" />
                              <span className="text-sm text-gray-900 dark:text-gray-100">{admin.shop_domain}</span>
                            </div>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">No store connected</span>
                        )}
                      </td>
                      <td className="py-4 px-4">
                        {admin.requires_reconnect ? (
                          <div className="flex items-center space-x-2">
                            <AlertTriangle className="h-4 w-4 text-amber-500" />
                            <span className="text-sm text-amber-600 dark:text-amber-400">Needs Reconnect</span>
                          </div>
                        ) : admin.has_connected_shop ? (
                          <div className="flex items-center space-x-2">
                            <CheckCircle className="h-4 w-4 text-green-500" />
                            <span className="text-sm text-green-600 dark:text-green-400">Connected</span>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">-</span>
                        )}
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-center justify-end space-x-2">
                          <Button
                            onClick={() => openConfigureModal(admin)}
                            variant="secondary"
                            size="sm"
                          >
                            <Settings className="h-4 w-4 mr-1" />
                            {admin.is_configured ? 'Update' : 'Configure'}
                          </Button>
                          {admin.is_configured && (
                            <Button
                              onClick={() => handleClearCredentials(admin.admin_id, admin.full_name || admin.email)}
                              variant="danger"
                              size="sm"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === 'postnl' && (
        <div className="card p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                PostNL API Credentials
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Configure PostNL shipping label API credentials for each admin account
              </p>
            </div>
            <Button onClick={loadPostNLCredentials} variant="secondary" loading={postnlCredentialsLoading}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>

          <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <div className="flex items-start space-x-3">
              <Package className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-blue-800 dark:text-blue-300">
                  PostNL API Setup
                </p>
                <p className="text-xs text-blue-700 dark:text-blue-400 mt-1">
                  Each admin needs their own PostNL API credentials to generate shipping labels.
                  You can get these from the PostNL Business Portal at{' '}
                  <a href="https://developer.postnl.nl" target="_blank" rel="noopener noreferrer" className="underline">
                    developer.postnl.nl
                  </a>
                </p>
              </div>
            </div>
          </div>

          {postnlCredentialsLoading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-6 w-6 animate-spin text-blue-500" />
            </div>
          ) : postnlCredentials.length === 0 ? (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              No admin accounts found
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-700 dark:text-gray-300">Admin</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-700 dark:text-gray-300">PostNL Credentials</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-700 dark:text-gray-300">Environment</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-700 dark:text-gray-300">Status</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-gray-700 dark:text-gray-300">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {postnlCredentials.map((admin) => (
                    <tr key={admin.admin_id} className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                      <td className="py-4 px-4">
                        <div>
                          <p className="font-medium text-gray-900 dark:text-gray-100">{admin.full_name || 'Unnamed'}</p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">{admin.email}</p>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        {admin.is_configured ? (
                          <div>
                            <div className="flex items-center space-x-2">
                              <Truck className="h-4 w-4 text-orange-500" />
                              <span className="text-sm text-gray-900 dark:text-gray-100">Configured</span>
                            </div>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                              Code: {admin.customer_code_masked}
                            </p>
                            {admin.configured_at && (
                              <p className="text-xs text-gray-400 dark:text-gray-500">
                                Set {new Date(admin.configured_at).toLocaleDateString()}
                              </p>
                            )}
                          </div>
                        ) : (
                          <div className="flex items-center space-x-2">
                            <Settings className="h-4 w-4 text-gray-400" />
                            <span className="text-sm text-gray-500 dark:text-gray-400">Not Configured</span>
                          </div>
                        )}
                      </td>
                      <td className="py-4 px-4">
                        {admin.is_configured ? (
                          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                            admin.environment === 'production'
                              ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                              : 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400'
                          }`}>
                            {admin.environment === 'production' ? 'Production' : 'Sandbox'}
                          </span>
                        ) : (
                          <span className="text-sm text-gray-400">-</span>
                        )}
                      </td>
                      <td className="py-4 px-4">
                        {admin.is_configured && admin.credentials_active ? (
                          <div className="flex items-center space-x-2">
                            <CheckCircle className="h-4 w-4 text-green-500" />
                            <span className="text-sm text-green-600 dark:text-green-400">Active</span>
                          </div>
                        ) : admin.is_configured ? (
                          <div className="flex items-center space-x-2">
                            <XCircle className="h-4 w-4 text-red-500" />
                            <span className="text-sm text-red-600 dark:text-red-400">Inactive</span>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">-</span>
                        )}
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-center justify-end space-x-2">
                          <Button
                            onClick={() => openPostNLConfigureModal(admin)}
                            variant="secondary"
                            size="sm"
                          >
                            <Settings className="h-4 w-4 mr-1" />
                            {admin.is_configured ? 'Update' : 'Configure'}
                          </Button>
                          {admin.is_configured && (
                            <Button
                              onClick={() => handleClearPostNLCredentials(admin.admin_id, admin.full_name || admin.email)}
                              variant="danger"
                              size="sm"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === 'brevo' && (
        <div className="card p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                Brevo Email & Sales History
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Configure Brevo email credentials and manage sales history access per admin
              </p>
            </div>
            <Button onClick={loadBrevoCredentials} variant="secondary" loading={brevoCredentialsLoading}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>

          {brevoCredentialsLoading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-6 w-6 animate-spin text-blue-500" />
            </div>
          ) : brevoCredentials.length === 0 ? (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              No admin accounts found
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-700 dark:text-gray-300">Admin</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-700 dark:text-gray-300">Brevo Credentials</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-700 dark:text-gray-300">Sales Access</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-700 dark:text-gray-300">Status</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-gray-700 dark:text-gray-300">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {brevoCredentials.map((admin) => (
                    <tr key={admin.admin_id} className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                      <td className="py-4 px-4">
                        <div>
                          <p className="font-medium text-gray-900 dark:text-gray-100">{admin.full_name || 'Unnamed'}</p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">{admin.email}</p>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        {admin.is_configured ? (
                          <div>
                            <div className="flex items-center space-x-2">
                              <CheckCircle className="h-4 w-4 text-green-500" />
                              <span className="text-sm text-gray-900 dark:text-gray-100">Configured</span>
                            </div>
                            {admin.sender_email_masked && (
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                Sender: {admin.sender_email_masked}
                              </p>
                            )}
                            {admin.sender_name && (
                              <p className="text-xs text-gray-400 dark:text-gray-500">
                                Name: {admin.sender_name}
                              </p>
                            )}
                            {admin.configured_at && (
                              <p className="text-xs text-gray-400 dark:text-gray-500">
                                Set {new Date(admin.configured_at).toLocaleDateString()}
                              </p>
                            )}
                          </div>
                        ) : (
                          <div className="flex items-center space-x-2">
                            <Settings className="h-4 w-4 text-gray-400" />
                            <span className="text-sm text-gray-500 dark:text-gray-400">Not Configured</span>
                          </div>
                        )}
                      </td>
                      <td className="py-4 px-4">
                        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                          admin.can_access_sales_history
                            ? 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-400'
                            : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                        }`}>
                          {admin.can_access_sales_history ? 'Enabled' : 'Disabled'}
                        </span>
                      </td>
                      <td className="py-4 px-4">
                        {admin.is_configured && admin.credentials_active ? (
                          <div className="flex items-center space-x-2">
                            <CheckCircle className="h-4 w-4 text-green-500" />
                            <span className="text-sm text-green-600 dark:text-green-400">Active</span>
                          </div>
                        ) : admin.is_configured ? (
                          <div className="flex items-center space-x-2">
                            <XCircle className="h-4 w-4 text-red-500" />
                            <span className="text-sm text-red-600 dark:text-red-400">Inactive</span>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">-</span>
                        )}
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-center justify-end space-x-2">
                          <Button
                            onClick={() => {
                              setConfiguringBrevoAdmin(admin);
                              setBrevoFormData({ api_key: '', sender_name: '', sender_email: '' });
                              setShowBrevoApiKey(false);
                              setShowConfigureBrevo(true);
                            }}
                            variant="secondary"
                            size="sm"
                          >
                            <Settings className="h-4 w-4 mr-1" />
                            {admin.is_configured ? 'Update' : 'Configure'}
                          </Button>
                          {admin.is_configured && (
                            <Button
                              onClick={() => handleClearBrevoCredentials(admin.admin_id, admin.full_name || admin.email)}
                              variant="danger"
                              size="sm"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === 'ai_log' && (
        <div className="card p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">AI Address fixes & warnings</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Only rows are stored when something actually changed or when the model reported an error/warning. Each entry saves the changed fields only—not a full copy of both addresses.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <input
                type="text"
                placeholder="Filter by country (e.g. ES)"
                value={aiLogsCountryFilter}
                onChange={(e) => { setAiLogsCountryFilter(e.target.value); setAiLogsPage(1); }}
                className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 w-40"
              />
              <button onClick={fetchAiLogs} className="p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300" title="Refresh">
                <RefreshCw className={`h-4 w-4 ${aiLogsLoading ? 'animate-spin' : ''}`} />
              </button>
              <button
                onClick={downloadAiTrainingLog}
                disabled={aiLogsDownloading || aiLogsTotalCount === 0}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40"
                title="Download training log (.jsonl)"
              >
                <Download className={`h-4 w-4 ${aiLogsDownloading ? 'animate-pulse' : ''}`} />
                Export
              </button>
            </div>
          </div>

          <div className="flex items-center justify-between mb-3">
            <p className="text-xs text-gray-500 dark:text-gray-400">{aiLogsTotalCount} total entries</p>
            <div className="flex items-center gap-2">
              {aiLogsSelected.size > 0 && (
                <button
                  onClick={deleteSelectedAiLogs}
                  disabled={aiLogsDeleting}
                  className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-medium rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-40"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  Delete {aiLogsSelected.size} selected
                </button>
              )}
              {aiLogsTotalCount > 0 && (
                <button
                  onClick={deleteAllAiLogs}
                  disabled={aiLogsDeleting}
                  className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-medium rounded-lg border border-red-300 dark:border-red-700 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-40"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  Delete all{aiLogsCountryFilter ? ` (${aiLogsCountryFilter.toUpperCase()})` : ''}
                </button>
              )}
            </div>
          </div>

          {aiLogsLoading ? (
            <div className="flex justify-center py-12">
              <RefreshCw className="h-6 w-6 animate-spin text-blue-500" />
            </div>
          ) : aiLogs.length === 0 ? (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              <Brain className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p>No fixes or warnings logged yet.</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                      <th className="py-2 pr-2 w-8">
                        <input
                          type="checkbox"
                          checked={aiLogs.length > 0 && aiLogsSelected.size === aiLogs.length}
                          onChange={toggleAiLogSelectAll}
                          className="rounded border-gray-300 dark:border-gray-600"
                        />
                      </th>
                      <th className="py-2 pr-3 font-medium">Date</th>
                      <th className="py-2 pr-3 font-medium">Admin</th>
                      <th className="py-2 pr-3 font-medium">Order</th>
                      <th className="py-2 pr-3 font-medium">Country</th>
                      <th className="py-2 pr-3 font-medium">Source</th>
                      <th className="py-2 pr-3 font-medium">Fixes</th>
                      <th className="py-2 font-medium w-10"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {aiLogs.map((log) => {
                      const isExpanded = aiLogsExpanded.has(log.id);
                      const issues = log.diagnostics?.issues || [];
                      const errorCount = issues.filter((i) => i.severity === 'error').length;
                      const warningCount = issues.filter((i) => i.severity === 'warning').length;
                      const origKeys = Object.keys(log.original_address || {}).filter((k) => String((log.original_address as Record<string, unknown>)[k] ?? '').trim());
                      const fixCount = origKeys.length;
                      return (
                        <React.Fragment key={log.id}>
                          <tr
                            className="hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer"
                            onClick={() => toggleAiLogExpand(log.id)}
                          >
                            <td className="py-2.5 pr-2" onClick={(e) => e.stopPropagation()}>
                              <input
                                type="checkbox"
                                checked={aiLogsSelected.has(log.id)}
                                onChange={() => toggleAiLogSelect(log.id)}
                                className="rounded border-gray-300 dark:border-gray-600"
                              />
                            </td>
                            <td className="py-2.5 pr-3 text-gray-600 dark:text-gray-300 whitespace-nowrap">
                              {new Date(log.created_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                            </td>
                            <td className="py-2.5 pr-3 text-gray-600 dark:text-gray-300 truncate max-w-[140px]">{log.admin_email}</td>
                            <td className="py-2.5 pr-3 text-gray-900 dark:text-gray-100 font-medium">{log.order_number || (log.shopify_order_id ? `#${log.shopify_order_id}` : '—')}</td>
                            <td className="py-2.5 pr-3">
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
                                {log.country_code || '??'}
                              </span>
                            </td>
                            <td className="py-2.5 pr-3 text-xs text-gray-500 dark:text-gray-400">{log.source || '—'}</td>
                            <td className="py-2.5 pr-3">
                              <div className="flex flex-wrap gap-1.5">
                                {fixCount > 0 && (
                                  <span className="text-xs px-1.5 py-0.5 rounded bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300">
                                    {fixCount} field{fixCount > 1 ? 's' : ''}
                                  </span>
                                )}
                                {errorCount > 0 && <span className="text-xs px-1.5 py-0.5 rounded bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400">{errorCount} error{errorCount > 1 ? 's' : ''}</span>}
                                {warningCount > 0 && <span className="text-xs px-1.5 py-0.5 rounded bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400">{warningCount} warn</span>}
                                {fixCount === 0 && errorCount === 0 && warningCount === 0 && <span className="text-xs text-gray-400">—</span>}
                              </div>
                            </td>
                            <td className="py-2.5 text-gray-400">
                              {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                            </td>
                          </tr>
                          {isExpanded && (
                            <tr>
                              <td colSpan={8} className="py-3 px-2 bg-gray-50 dark:bg-gray-800/30">
                                <div className="grid grid-cols-2 gap-4 text-xs">
                                  <div>
                                    <h4 className="font-semibold text-red-600 dark:text-red-400 mb-1.5">Before (changed fields only)</h4>
                                    <div className="bg-white dark:bg-gray-900 rounded p-3 border border-red-200 dark:border-red-800 font-mono whitespace-pre-wrap break-all min-h-[2.5rem]">
                                      {Object.keys(log.original_address || {}).length === 0 ? (
                                        <span className="text-gray-400">—</span>
                                      ) : (
                                        Object.entries(log.original_address || {}).map(([k, v]) => (
                                          <div key={k}><span className="text-gray-500">{k}:</span> {String(v)}</div>
                                        ))
                                      )}
                                    </div>
                                  </div>
                                  <div>
                                    <h4 className="font-semibold text-green-600 dark:text-green-400 mb-1.5">After (same keys)</h4>
                                    <div className="bg-white dark:bg-gray-900 rounded p-3 border border-green-200 dark:border-green-800 font-mono whitespace-pre-wrap break-all min-h-[2.5rem]">
                                      {(Object.keys(log.corrected_address || {}).length === 0) ? (
                                        <span className="text-gray-400">—</span>
                                      ) : (
                                        Object.entries(log.corrected_address || {}).map(([k, v]) => {
                                          const orig = String((log.original_address as Record<string, unknown>)[k] ?? '');
                                          const curr = String(v);
                                          const changed = orig !== curr;
                                          return (
                                            <div key={k} className={changed ? 'text-green-700 dark:text-green-400 font-semibold' : ''}>
                                              <span className="text-gray-500 font-normal">{k}:</span> {curr}
                                              {changed && <span className="ml-1 text-[10px] text-green-600 dark:text-green-500">(changed)</span>}
                                            </div>
                                          );
                                        })
                                      )}
                                    </div>
                                  </div>
                                </div>
                                {issues.length > 0 && (
                                  <div className="mt-3">
                                    <h4 className="font-semibold text-gray-700 dark:text-gray-300 mb-1.5 text-xs">Errors &amp; warnings (info messages are not stored)</h4>
                                    <div className="space-y-1">
                                      {issues.map((issue, idx) => (
                                        <div key={idx} className={`text-xs px-2 py-1 rounded ${
                                          issue.severity === 'error' ? 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400' :
                                          issue.severity === 'warning' ? 'bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-400' :
                                          'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400'
                                        }`}>
                                          [{issue.severity}] <strong>{issue.field}</strong>: {issue.message}
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {aiLogsTotalCount > AI_LOGS_PAGE_SIZE && (
                <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-200 dark:border-gray-700">
                  <p className="text-xs text-gray-500">Page {aiLogsPage} of {Math.ceil(aiLogsTotalCount / AI_LOGS_PAGE_SIZE)}</p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setAiLogsPage(p => Math.max(1, p - 1))}
                      disabled={aiLogsPage <= 1}
                      className="px-3 py-1 text-sm rounded border border-gray-300 dark:border-gray-600 disabled:opacity-40"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setAiLogsPage(p => p + 1)}
                      disabled={aiLogsPage >= Math.ceil(aiLogsTotalCount / AI_LOGS_PAGE_SIZE)}
                      className="px-3 py-1 text-sm rounded border border-gray-300 dark:border-gray-600 disabled:opacity-40"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {showConfigureBrevo && configuringBrevoAdmin && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-lg">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Configure Brevo Credentials
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {configuringBrevoAdmin.full_name || configuringBrevoAdmin.email}
                </p>
              </div>
              <Button
                onClick={() => {
                  setShowConfigureBrevo(false);
                  setConfiguringBrevoAdmin(null);
                  setBrevoFormData({ api_key: '', sender_name: '', sender_email: '' });
                  setShowBrevoApiKey(false);
                }}
                variant="secondary"
                size="sm"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            <form onSubmit={handleConfigureBrevo} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Brevo API Key *
                </label>
                <div className="relative">
                  <Input
                    type={showBrevoApiKey ? 'text' : 'password'}
                    value={brevoFormData.api_key}
                    onChange={(e) => setBrevoFormData(prev => ({ ...prev, api_key: e.target.value }))}
                    placeholder="xkeysib-..."
                    required
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
                    onClick={() => setShowBrevoApiKey(!showBrevoApiKey)}
                  >
                    {showBrevoApiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Sender Name
                </label>
                <Input
                  type="text"
                  value={brevoFormData.sender_name}
                  onChange={(e) => setBrevoFormData(prev => ({ ...prev, sender_name: e.target.value }))}
                  placeholder="My Store"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Sender Email *
                </label>
                <Input
                  type="email"
                  value={brevoFormData.sender_email}
                  onChange={(e) => setBrevoFormData(prev => ({ ...prev, sender_email: e.target.value }))}
                  placeholder="noreply@yourstore.com"
                  required
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Must be a verified sender in Brevo
                </p>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    setShowConfigureBrevo(false);
                    setConfiguringBrevoAdmin(null);
                    setBrevoFormData({ api_key: '', sender_name: '', sender_email: '' });
                    setShowBrevoApiKey(false);
                  }}
                >
                  Cancel
                </Button>
                <Button type="submit" variant="primary" loading={brevoSaving}>
                  Save Credentials
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showConfigurePostNL && configuringPostNLAdmin && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Configure PostNL Credentials
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {configuringPostNLAdmin.full_name || configuringPostNLAdmin.email}
                </p>
              </div>
              <Button
                onClick={() => {
                  setShowConfigurePostNL(false);
                  setConfiguringPostNLAdmin(null);
                  resetPostNLForm();
                }}
                variant="secondary"
                size="sm"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            <form onSubmit={handleConfigurePostNL} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  API Key *
                </label>
                <div className="relative">
                  <Input
                    type={showPostNLApiKey ? 'text' : 'password'}
                    value={postnlFormData.api_key}
                    onChange={(e) => setPostnlFormData(prev => ({ ...prev, api_key: e.target.value }))}
                    placeholder="Enter PostNL API Key"
                    required
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
                    onClick={() => setShowPostNLApiKey(!showPostNLApiKey)}
                  >
                    {showPostNLApiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Customer Code *
                </label>
                <Input
                  type="text"
                  value={postnlFormData.customer_code}
                  onChange={(e) => setPostnlFormData(prev => ({ ...prev, customer_code: e.target.value }))}
                  placeholder="e.g., DEVC"
                  required
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Your PostNL customer code (usually 4-8 characters)
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Customer Number (Optional)
                </label>
                <Input
                  type="text"
                  value={postnlFormData.customer_number}
                  onChange={(e) => setPostnlFormData(prev => ({ ...prev, customer_number: e.target.value }))}
                  placeholder="Enter customer number"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Collection Location (Optional)
                </label>
                <Input
                  type="text"
                  value={postnlFormData.collection_location}
                  onChange={(e) => setPostnlFormData(prev => ({ ...prev, collection_location: e.target.value }))}
                  placeholder="Location code for pickup"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Contact Person (Optional)
                </label>
                <Input
                  type="text"
                  value={postnlFormData.contact_person}
                  onChange={(e) => setPostnlFormData(prev => ({ ...prev, contact_person: e.target.value }))}
                  placeholder="Contact name for shipments"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Notification Email (Optional)
                </label>
                <Input
                  type="email"
                  value={postnlFormData.email}
                  onChange={(e) => setPostnlFormData(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="Email for PostNL notifications"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Environment *
                </label>
                <select
                  value={postnlFormData.environment}
                  onChange={(e) => setPostnlFormData(prev => ({ ...prev, environment: e.target.value as 'sandbox' | 'production' }))}
                  className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-gray-900 dark:text-gray-100"
                >
                  <option value="sandbox">Sandbox (Testing)</option>
                  <option value="production">Production (Live)</option>
                </select>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Use Sandbox for testing, Production for real shipments
                </p>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    setShowConfigurePostNL(false);
                    setConfiguringPostNLAdmin(null);
                    resetPostNLForm();
                  }}
                >
                  Cancel
                </Button>
                <Button type="submit" variant="primary" loading={postnlSaving}>
                  Save Credentials
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showConfigureCredentials && configuringAdmin && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-lg">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Configure Shopify Credentials
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {configuringAdmin.full_name || configuringAdmin.email}
                </p>
              </div>
              <Button
                onClick={() => {
                  setShowConfigureCredentials(false);
                  setConfiguringAdmin(null);
                  resetCredentialForm();
                }}
                variant="secondary"
                size="sm"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            {configuringAdmin.has_connected_shop && (
              <div className="mb-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                <div className="flex items-start space-x-2">
                  <AlertTriangle className="h-5 w-5 text-amber-500 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                      Connected Store Warning
                    </p>
                    <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
                      This admin has a connected store ({configuringAdmin.shop_domain}). Changing credentials will require them to reconnect.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <form onSubmit={handleConfigureCredentials} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Client ID (API Key) *
                </label>
                <Input
                  type="text"
                  value={credentialFormData.client_id}
                  onChange={(e) => setCredentialFormData(prev => ({ ...prev, client_id: e.target.value }))}
                  placeholder="Enter Shopify API Key"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Client Secret (API Secret) *
                </label>
                <div className="relative">
                  <Input
                    type={showClientSecret ? 'text' : 'password'}
                    value={credentialFormData.client_secret}
                    onChange={(e) => setCredentialFormData(prev => ({ ...prev, client_secret: e.target.value }))}
                    placeholder="Enter Shopify API Secret (min 32 chars)"
                    required
                    minLength={32}
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
                    onClick={() => setShowClientSecret(!showClientSecret)}
                  >
                    {showClientSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Webhook Secret (Optional)
                </label>
                <Input
                  type="password"
                  value={credentialFormData.webhook_secret}
                  onChange={(e) => setCredentialFormData(prev => ({ ...prev, webhook_secret: e.target.value }))}
                  placeholder="Leave empty to use Client Secret"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  If empty, Client Secret will be used for webhook verification
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  OAuth Scopes
                </label>
                <Input
                  type="text"
                  value={credentialFormData.scopes}
                  onChange={(e) => setCredentialFormData(prev => ({ ...prev, scopes: e.target.value }))}
                  placeholder="Comma-separated scopes"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Standard scopes are pre-filled. Only modify if needed.
                </p>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    setShowConfigureCredentials(false);
                    setConfiguringAdmin(null);
                    resetCredentialForm();
                  }}
                >
                  Cancel
                </Button>
                <Button type="submit" variant="primary" loading={credentialSaving}>
                  Save Credentials
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showEditAdmin && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Edit Admin
              </h3>
              <Button
                onClick={resetEditAdminForm}
                variant="secondary"
                size="sm"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            <form onSubmit={handleEditAdmin} className="space-y-4">
              <Input
                label="Full Name *"
                type="text"
                value={editAdminData.full_name}
                onChange={(e) => setEditAdminData({ ...editAdminData, full_name: e.target.value })}
                placeholder="Enter full name"
                required
              />

              <Input
                label="Email *"
                type="email"
                value={editAdminData.email}
                onChange={(e) => setEditAdminData({ ...editAdminData, email: e.target.value })}
                placeholder="admin@example.com"
                required
              />

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Password (leave blank to keep current)
                </label>
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={editAdminData.password}
                    onChange={(e) => setEditAdminData({ ...editAdminData, password: e.target.value })}
                    placeholder="Min 8 characters"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              {editAdminData.password && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Confirm Password *
                  </label>
                  <div className="relative">
                    <Input
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={editAdminData.confirmPassword}
                      onChange={(e) => setEditAdminData({ ...editAdminData, confirmPassword: e.target.value })}
                      placeholder="Confirm password"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
              )}

              <div className="flex space-x-3 pt-4">
                <Button
                  type="submit"
                  variant="primary"
                  loading={createAdminLoading}
                  className="flex-1"
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Update Admin
                </Button>
                <Button
                  type="button"
                  onClick={resetEditAdminForm}
                  variant="secondary"
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showCreateAdmin && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Create New Admin
              </h3>
              <Button
                onClick={resetCreateAdminForm}
                variant="secondary"
                size="sm"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            <form onSubmit={handleCreateAdmin} className="space-y-4">
              <Input
                label="Full Name *"
                type="text"
                value={newAdminData.full_name}
                onChange={(e) => setNewAdminData({ ...newAdminData, full_name: e.target.value })}
                placeholder="Enter full name"
                required
              />

              <Input
                label="Email *"
                type="email"
                value={newAdminData.email}
                onChange={(e) => setNewAdminData({ ...newAdminData, email: e.target.value })}
                placeholder="admin@example.com"
                required
              />

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Password *
                </label>
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={newAdminData.password}
                    onChange={(e) => setNewAdminData({ ...newAdminData, password: e.target.value })}
                    placeholder="Min 8 characters"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Confirm Password *
                </label>
                <div className="relative">
                  <Input
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={newAdminData.confirmPassword}
                    onChange={(e) => setNewAdminData({ ...newAdminData, confirmPassword: e.target.value })}
                    placeholder="Confirm password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div className="flex space-x-3 pt-4">
                <Button
                  type="submit"
                  variant="primary"
                  loading={createAdminLoading}
                  className="flex-1"
                >
                  <UserPlus className="h-4 w-4 mr-2" />
                  Create Admin
                </Button>
                <Button
                  type="button"
                  onClick={resetCreateAdminForm}
                  variant="secondary"
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
